import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Download, Search, Tag } from "lucide-react";

const blank = {
  code: "", discount_type: "percentage", discount_value: 10,
  min_order: 0, max_discount: 0, valid_from: "", valid_till: "",
  usage_limit_total: 0, usage_limit_per_user: 0, applicable_to: "all", active: true,
};

const isExpired = (c) => c.valid_till && new Date(c.valid_till) < new Date(new Date().toDateString());

export default function CouponsTab({ content }) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null); // null | coupon | "new"
  const [formData, setFormData] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [deleteCoupon, setDeleteCoupon] = useState(null);

  const applicableOptions = useMemo(() => {
    const opts = [
      { value: "all", label: "All Passes" },
      { value: "day", label: "Day Pass (all tiers)" },
      { value: "meraki", label: "Meraki Pass (all tiers)" },
    ];
    (content?.day_pass?.pricing?.pass_types || []).forEach((t) =>
      opts.push({ value: `day:${t.key}`, label: `Day Pass · ${t.label}` }));
    (content?.meraki_pass?.pricing?.pass_types || []).forEach((t) =>
      opts.push({ value: `meraki:${t.key}`, label: `Meraki Pass · ${t.label}` }));
    return opts;
  }, [content]);

  const labelFor = (val) => applicableOptions.find((o) => o.value === val)?.label || val;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/coupons");
      setCoupons(data);
    } catch (e) {
      toast.error("Failed to load coupons");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return coupons.filter((c) => {
      if (q && !c.code.toLowerCase().includes(q)) return false;
      if (statusFilter === "active" && !(c.active && !isExpired(c))) return false;
      if (statusFilter === "inactive" && c.active) return false;
      if (statusFilter === "expired" && !isExpired(c)) return false;
      return true;
    });
  }, [coupons, search, statusFilter]);

  const openNew = () => { setFormData(blank); setEditing("new"); };
  const openEdit = (c) => { setFormData({ ...blank, ...c }); setEditing(c); };
  const setF = (k, v) => setFormData((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        discount_value: Number(formData.discount_value),
        min_order: Number(formData.min_order || 0),
        max_discount: Number(formData.max_discount || 0),
        usage_limit_total: Number(formData.usage_limit_total || 0),
        usage_limit_per_user: Number(formData.usage_limit_per_user || 0),
      };
      if (editing === "new") await api.post("/coupons", payload);
      else await api.put(`/coupons/${editing.id}`, payload);
      toast.success(editing === "new" ? "Coupon created" : "Coupon updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to save coupon");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.put(`/coupons/${c.id}`, { ...c, active: !c.active });
      setCoupons((list) => list.map((x) => (x.id === c.id ? { ...x, active: !c.active } : x)));
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/coupons/${deleteCoupon.id}`);
      toast.success("Coupon deleted");
      setDeleteCoupon(null);
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const exportCsv = () => {
    const cols = ["code", "discount_value", "min_order", "max_discount", "valid_from", "valid_till", "usage_limit_total", "usage_limit_per_user", "usage_count", "applicable_to", "active"];
    const header = cols.join(",");
    const rows = filtered.map((c) => cols.map((k) => `${c[k] ?? ""}`).join(","));
    const blob = new Blob([[header, ...rows].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "coupons.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#71717A]" />
            <Input data-testid="coupon-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search code" className="pl-9 w-48" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="coupon-status-filter" className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-border">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button data-testid="coupon-export" onClick={exportCsv} variant="outline" className="border-border"><Download className="h-4 w-4 mr-1" /> CSV</Button>
          <Button data-testid="coupon-new" onClick={openNew} className="bg-[#FF4500] hover:bg-[#FF5722]"><Plus className="h-4 w-4 mr-1" /> New Coupon</Button>
        </div>
      </div>

      <div className="rounded-xl border border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>Code</TableHead>
              <TableHead>Discount</TableHead>
              <TableHead>Validity</TableHead>
              <TableHead>Usage</TableHead>
              <TableHead>Applicable</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#71717A]"><Loader2 className="h-5 w-5 animate-spin inline" /></TableCell></TableRow>}
            {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#71717A]">No coupons yet. Create your first coupon.</TableCell></TableRow>}
            {!loading && filtered.map((c) => (
              <TableRow key={c.id} className="border-border" data-testid={`coupon-row-${c.code}`}>
                <TableCell className="font-semibold"><span className="inline-flex items-center gap-1.5"><Tag className="h-3.5 w-3.5 text-[#FF4500]" />{c.code}</span></TableCell>
                <TableCell>{c.discount_value}%{Number(c.max_discount) > 0 ? ` (max ₹${c.max_discount})` : ""}</TableCell>
                <TableCell className="text-xs text-[#A1A1AA]">{c.valid_from || "—"} → {c.valid_till || "—"}{isExpired(c) && <Badge className="ml-1 bg-red-500/20 text-red-300 border-0">Expired</Badge>}</TableCell>
                <TableCell className="text-[#A1A1AA]">{c.usage_count || 0}{Number(c.usage_limit_total) > 0 ? ` / ${c.usage_limit_total}` : ""}</TableCell>
                <TableCell className="text-xs text-[#A1A1AA]">{labelFor(c.applicable_to)}</TableCell>
                <TableCell><Switch data-testid={`coupon-toggle-${c.code}`} checked={!!c.active} onCheckedChange={() => toggleActive(c)} /></TableCell>
                <TableCell>
                  <div className="flex justify-end gap-1">
                    <Button data-testid={`coupon-edit-${c.code}`} onClick={() => openEdit(c)} variant="ghost" size="icon" className="h-8 w-8 hover:bg-white/10"><Pencil className="h-4 w-4" /></Button>
                    <Button data-testid={`coupon-delete-${c.code}`} onClick={() => setDeleteCoupon(c)} variant="ghost" size="icon" className="h-8 w-8 hover:bg-red-500/15 text-red-400"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#121212] border-border max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing === "new" ? "Create Coupon" : "Edit Coupon"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Coupon Code *" className="col-span-2">
              <Input data-testid="coupon-form-code" value={formData.code} onChange={(e) => setF("code", e.target.value.toUpperCase())} placeholder="MERAKI10" className="uppercase" />
            </Field>
            <Field label="Discount % *">
              <Input data-testid="coupon-form-value" type="number" min={1} max={100} value={formData.discount_value} onChange={(e) => setF("discount_value", e.target.value)} />
            </Field>
            <Field label="Max Discount Cap (₹)">
              <Input data-testid="coupon-form-maxcap" type="number" min={0} value={formData.max_discount} onChange={(e) => setF("max_discount", e.target.value)} placeholder="0 = no cap" />
            </Field>
            <Field label="Minimum Order Amount (₹)">
              <Input data-testid="coupon-form-minorder" type="number" min={0} value={formData.min_order} onChange={(e) => setF("min_order", e.target.value)} placeholder="0 = none" />
            </Field>
            <Field label="Applicable To">
              <Select value={formData.applicable_to} onValueChange={(v) => setF("applicable_to", v)}>
                <SelectTrigger data-testid="coupon-form-applicable"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-border max-h-64">
                  {applicableOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Valid From *">
              <Input data-testid="coupon-form-from" type="date" value={formData.valid_from} onChange={(e) => setF("valid_from", e.target.value)} />
            </Field>
            <Field label="Valid Till *">
              <Input data-testid="coupon-form-till" type="date" value={formData.valid_till} onChange={(e) => setF("valid_till", e.target.value)} />
            </Field>
            <Field label="Usage Limit (Total)">
              <Input data-testid="coupon-form-limit-total" type="number" min={0} value={formData.usage_limit_total} onChange={(e) => setF("usage_limit_total", e.target.value)} placeholder="0 = unlimited" />
            </Field>
            <Field label="Usage Limit (Per User)">
              <Input data-testid="coupon-form-limit-user" type="number" min={0} value={formData.usage_limit_per_user} onChange={(e) => setF("usage_limit_per_user", e.target.value)} placeholder="0 = unlimited" />
            </Field>
            <div className="col-span-2 flex items-center gap-3 pt-1">
              <Switch data-testid="coupon-form-active" checked={!!formData.active} onCheckedChange={(v) => setF("active", v)} />
              <Label className="text-sm">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-border">Cancel</Button>
            <Button data-testid="coupon-form-save" onClick={save} disabled={saving} className="bg-[#FF4500] hover:bg-[#FF5722]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Coupon"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteCoupon} onOpenChange={(o) => !o && setDeleteCoupon(null)}>
        <AlertDialogContent className="bg-[#121212] border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete coupon {deleteCoupon?.code}?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone. Past orders keep their recorded discount.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="coupon-delete-confirm" onClick={doDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, className, children }) {
  return (
    <div className={`space-y-1.5 ${className || ""}`}>
      <Label className="text-xs uppercase tracking-wide text-[#A1A1AA]">{label}</Label>
      {children}
    </div>
  );
}
